import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { cn } from '@/core/lib/utils';
import { useUIStore } from '@/core/stores/ui.store';
import { useInstantFormat } from '@/shared/hooks/useInstantFormat';
import type { AllTaskItemDto } from '../../types';

interface AllTasksTableRowProps {
  task: AllTaskItemDto;
}

const PRIORITY_LABELS = ['none', 'low', 'medium', 'high', 'urgent'] as const;

const PRIORITY_STYLES: Record<number, string> = {
  0: 'bg-muted text-muted-foreground',
  1: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300',
  2: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300',
  3: 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
  4: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400',
};

const STATUS_STYLES: Record<string, string> = {
  Todo: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  InProgress: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  Done: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
};

const AVATAR_COLORS = [
  'bg-cyan-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-orange-400',
  'bg-pink-400',
  'bg-blue-500',
  'bg-amber-400',
];

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function getAvatarColor(name: string | null): string {
  if (!name) return 'bg-muted';
  const sum = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function formatHours(decimal: number): string {
  const h = Math.floor(decimal);
  const m = Math.round((decimal - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** Calendar day (yyyy-MM-dd) of an instant as seen in the given timezone. */
function ymdInTz(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/** Whole days overdue, compared by calendar day in the user's timezone (no browser-tz drift). */
function getDelayDays(dueDateStr: string | null, timezone: string): number {
  if (!dueDateStr) return 0;
  const due = new Date(`${ymdInTz(dueDateStr, timezone)}T12:00:00`).getTime();
  const today = new Date(`${ymdInTz(new Date().toISOString(), timezone)}T12:00:00`).getTime();
  return Math.max(0, Math.round((today - due) / 86400000));
}

export function AllTasksTableRow({ task }: AllTasksTableRowProps) {
  const { t } = useTranslation('analytics');
  const formatInstant = useInstantFormat();
  const timezone = useUIStore((s) => s.timezone);

  const priorityKey = PRIORITY_LABELS[task.priority] ?? 'none';
  const priorityStyle = PRIORITY_STYLES[task.priority] ?? PRIORITY_STYLES[0];
  const statusStyle = STATUS_STYLES[task.statusType ?? ''] ?? STATUS_STYLES.Todo;
  const statusLabel = task.statusName ?? t(`statusType.${task.statusType ?? 'Todo'}`);

  // Analytics dueDate is a plain date-time Instant (NOT a PlannedDate).
  const shortDate = task.dueDate ? formatInstant(task.dueDate) : '—';
  const delayDays = task.isOverdue ? getDelayDays(task.dueDate, timezone) : 0;

  const hasTracked = task.trackedHours > 0;

  return (
    <tr className="border-b last:border-0 hover:bg-accent/30">
      {/* Task */}
      <td className="max-w-[200px] py-3 pl-4 pr-2">
        <div className="flex items-center gap-1.5">
          {task.isOverdue && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />}
          <span
            className={cn('line-clamp-1 text-sm font-medium', task.isOverdue && 'text-destructive')}
          >
            {task.title ?? '—'}
          </span>
        </div>
      </td>

      {/* Project */}
      <td className="max-w-[160px] px-2 py-3">
        <div className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: task.projectColor ?? '#6b7280' }}
          />
          <span className="min-w-0 truncate text-sm text-muted-foreground">
            {task.projectName ?? '—'}
          </span>
        </div>
      </td>

      {/* Assignee */}
      <td className="max-w-[160px] px-2 py-3">
        <div className="flex items-center gap-1.5">
          <Avatar className="h-7 w-7 shrink-0">
            <AvatarImage src={task.assigneeAvatarUrl ?? undefined} />
            <AvatarFallback
              className={cn('text-xs font-semibold text-white', getAvatarColor(task.assigneeName))}
            >
              {getInitials(task.assigneeName)}
            </AvatarFallback>
          </Avatar>
          <span className="min-w-0 truncate text-sm">{task.assigneeName ?? '—'}</span>
        </div>
      </td>

      {/* Status */}
      <td className="px-2 py-3">
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
            statusStyle,
          )}
        >
          {statusLabel}
        </span>
      </td>

      {/* Priority */}
      <td className="px-2 py-3">
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
            priorityStyle,
          )}
        >
          {t(`priority.${priorityKey}`)}
        </span>
      </td>

      {/* Due date */}
      <td className="px-2 py-3 text-sm">
        {task.isOverdue && delayDays > 0 ? (
          <span className="font-medium text-destructive">
            {shortDate} (+{delayDays}d)
          </span>
        ) : (
          <span className="text-muted-foreground">{shortDate}</span>
        )}
      </td>

      {/* Est. */}
      <td className="px-2 py-3 text-right text-sm text-muted-foreground">
        {formatHours(task.estimatedHours)}
      </td>

      {/* Real */}
      <td className="px-2 py-3 text-right text-sm">
        <span
          className={
            hasTracked ? 'font-medium text-cyan-600 dark:text-cyan-400' : 'text-muted-foreground'
          }
        >
          {formatHours(task.trackedHours)}
        </span>
      </td>

      {/* Efficiency */}
      <td className="px-2 py-3 pr-4 text-right text-sm">
        {hasTracked ? (
          <span className="font-bold text-primary">{task.efficiencyPercentage.toFixed(0)}%</span>
        ) : null}
      </td>
    </tr>
  );
}
